import { useMemo } from "react";
import { BusinessLine } from "./CategoryPerformanceView";
import { CATEGORY_GROUPS } from "./constants";
import { defaultBusinessLines } from "./mockData";

interface UseCategoryPerformanceProps {
  data?: any;
  viewType: "chart" | "table";
  selectedCategory: string;
}

export const useCategoryPerformance = ({
  data,
  viewType,
  selectedCategory,
}: UseCategoryPerformanceProps) => {
  const PALETTE_LENGTH = 13;

  const getColorForProduct = (name: string, index: number): string => {
    const colorMap: Record<string, string> = {
      Cleanser: "var(--data-vis-data-vis-1)",
      "Eye Cosmetics": "var(--data-vis-data-vis-2)",
      "Face Cosmetics": "var(--data-vis-data-vis-3)",
      "Lip Cosmetics": "var(--data-vis-data-vis-4)",
      Mask: "var(--data-vis-data-vis-5)",
      Moisturizer: "var(--data-vis-data-vis-6)",
      Toner: "var(--data-vis-data-vis-7)",
      Treatment: "var(--data-vis-data-vis-10)",
      "Devices-Skin Care": "var(--data-vis-data-vis-8)",
      "Cosmetics-Other": "#F16A81",
    };
    return (
      colorMap[name] ||
      `var(--data-vis-data-vis-${(index % PALETTE_LENGTH) + 1})`
    );
  };

  const chartData: BusinessLine[] = useMemo(() => {
    if (viewType !== "chart") return [];

    const rawData = Array.isArray(data) ? data : defaultBusinessLines;

    let filtered = rawData;
    if (selectedCategory !== "All") {
      filtered = rawData.filter(
        (line: BusinessLine) => line.category === selectedCategory
      );
    }

    const sorted = [...filtered].sort(
      (a: BusinessLine, b: BusinessLine) => b.revenue - a.revenue
    );
    return sorted.map((line: BusinessLine, index: number) => ({
      ...line,
      color: getColorForProduct(line.name, index),
    }));
  }, [data, selectedCategory, viewType]);

  const top10BusinessLines = useMemo(() => {
    return chartData.slice(0, 10);
  }, [chartData]);

  const totalRevenue = useMemo(() => {
    return top10BusinessLines.reduce((sum, line) => sum + line.revenue, 0);
  }, [top10BusinessLines]);

  const restProductsData = useMemo(() => {
    const restLines = chartData.slice(10);
    const restRevenue = restLines.reduce((sum, line) => sum + line.revenue, 0);
    const restBV = restLines.reduce((sum, line) => sum + line.bvValue, 0);
    if (restRevenue === 0 && restBV === 0 && chartData.length > 0) {
      const lastItem = chartData[chartData.length - 1];
      return { revenue: lastItem.revenue, bvValue: lastItem.bvValue };
    }
    return { revenue: restRevenue, bvValue: restBV };
  }, [chartData]);

  const tableData = useMemo(() => {
    if (viewType !== "table") return null;

    const rawTableData = data?.detail?.chart?.chartDetail?.tableDetail;
    if (!rawTableData) return rawTableData;

    const selectedKey = selectedCategory.toLowerCase();
    const allowedCategories = CATEGORY_GROUPS[selectedKey] ?? [selectedKey];

    const filteredRows = rawTableData?.body?.rows?.filter((row: any) => {
      if (selectedCategory === "All") return true;

      if (row[0]?.groupName) {
        const group = row[0].groupName.toLowerCase();
        return (
          group === selectedKey ||
          (selectedCategory === "Beauty" && group === "beauty")
        );
      }
      const categoryName = row[0]?.kpiValue?.toLowerCase();
      return allowedCategories.includes(categoryName);
    });

    return {
      ...rawTableData,
      body: {
        ...rawTableData.body,
        rows: filteredRows || [],
      },
    };
  }, [data, selectedCategory, viewType]);

  return {
    chartData,
    top10BusinessLines,
    totalRevenue,
    restProductsData,
    tableData,
  };
};
